import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Bike,
  ClipboardList,
  Package,
  Star,
  Store,
  TrendingUp,
  UtensilsCrossed,
  Power,
} from 'lucide-react';
import { getErrorMessage } from '@/lib/api';
import { esAdmin, useAuth } from '@/lib/auth-store';
import { MODULO_POR_RUTA, usePuede } from '@/lib/permisos';
import { cn, formatCOP } from '@/lib/cn';
import {
  useAjustes,
  useComercio,
  useGuardarAjustes,
  useMerchantId,
  useOperacion,
  useProductos,
  useReclamosAbiertos,
  useSecciones,
  type Ajustes as AjustesPlataforma,
} from '@/lib/queries';
import { Boton, Cargando, ConfirmarModal, ErrorCarga } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

export function Resumen() {
  const { user } = useAuth();
  return esAdmin(user?.role) ? <ResumenAdmin /> : <ResumenComercio />;
}

// ── Comercio ────────────────────────────────────────────────

function ResumenComercio() {
  const merchantId = useMerchantId();
  const comercio = useComercio(merchantId);
  const productos = useProductos(merchantId);
  const secciones = useSecciones(merchantId);
  const { user } = useAuth();

  if (comercio.isLoading) return <Cargando />;
  if (comercio.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(comercio.error)} onReintentar={() => comercio.refetch()} />
    );
  }

  const total = productos.data?.length ?? 0;
  const agotados = productos.data?.filter((p) => !p.isAvailable).length ?? 0;
  const sinFoto = productos.data?.filter((p) => !p.imageUrl).length ?? 0;
  const menuVacio = total === 0;

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-3xl text-kick-dark">Hola, {user?.fullName.split(' ')[0]}</h1>
      <p className="mt-1 text-sm text-kick-gray">{comercio.data?.name}</p>

      {/* Avisos que requieren acción */}
      <div className="mt-6 space-y-3">
        {menuVacio && (
          <Aviso
            tono="alerta"
            titulo="Tu menú está vacío"
            texto="Los clientes no pueden pedirte nada hasta que cargues productos."
            accion={{ texto: 'Cargar el menú', a: '/menu' }}
          />
        )}

        {!menuVacio && !comercio.data?.isOpenNow && (
          <Aviso
            tono="info"
            titulo="Tu comercio está cerrado"
            texto="Usa el interruptor de la barra superior para empezar a recibir pedidos."
          />
        )}

        {agotados > 0 && (
          <Aviso
            tono="alerta"
            titulo={`${agotados} producto(s) agotado(s)`}
            texto="No aparecen disponibles en la tienda."
            accion={{ texto: 'Revisar el menú', a: '/menu' }}
          />
        )}

        {sinFoto > 0 && !menuVacio && (
          <Aviso
            tono="info"
            titulo={`${sinFoto} producto(s) sin foto`}
            texto="Los productos con foto se piden bastante más."
            accion={{ texto: 'Agregar fotos', a: '/menu' }}
          />
        )}
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metrica
          icono={<Package className="h-5 w-5" />}
          etiqueta="Productos"
          valor={String(total)}
          detalle={`${(secciones.data?.length ?? 0)} secciones`}
        />
        <Metrica
          icono={<UtensilsCrossed className="h-5 w-5" />}
          etiqueta="Disponibles"
          valor={String(total - agotados)}
          detalle={agotados > 0 ? `${agotados} agotados` : 'todos disponibles'}
        />
        <Metrica
          icono={<Star className="h-5 w-5" />}
          etiqueta="Calificación"
          valor={comercio.data ? Number(comercio.data.ratingAvg).toFixed(1) : '—'}
          detalle={`${comercio.data?.ratingCount ?? 0} reseñas`}
        />
        <Metrica
          icono={<Store className="h-5 w-5" />}
          etiqueta="Estado"
          valor={comercio.data?.isOpenNow ? 'Abierto' : 'Cerrado'}
          detalle={comercio.data?.status === 'ACTIVE' ? 'activo en la plataforma' : 'no publicado'}
          resaltado={comercio.data?.isOpenNow}
        />
      </div>

      <div className="mt-6 rounded-kick border border-dashed border-kick-gray/30 p-6 text-center">
        <p className="font-semibold text-kick-ink">Los pedidos entran en el tablero</p>
        <p className="mt-1 text-sm text-kick-gray">
          Ahí los aceptas, marcas cuándo están listos y los despachas.
        </p>
        <Link to="/pedidos" className="mt-4 inline-block">
          <Boton icono={<ClipboardList className="h-4 w-4" />}>Ir al tablero</Boton>
        </Link>
      </div>
    </div>
  );
}

// ── Administración ──────────────────────────────────────────

/**
 * La portada de administración.
 *
 * Responde dos preguntas, en ese orden: **¿hay algo que atender ahora?** y
 * ¿cómo va el día? Lo urgente va arriba y solo aparece cuando existe; una
 * pantalla que muestra «0 pedidos sin domiciliario» todos los días enseña a no
 * mirarla, y el día que diga 3 tampoco se mira.
 */
function ResumenAdmin() {
  const { user } = useAuth();
  const puede = usePuede();
  const veOperacion = puede('operacion', 'ver');
  const operacion = useOperacion(true, veOperacion);
  const reclamos = useReclamosAbiertos(puede('reclamos', 'ver'));

  /**
   * Un administrador sin permiso de operación no ve el tablero: ve una
   * portada con los atajos a lo suyo. Sin esto, su primera pantalla sería
   * un error 403, que se lee como «el sistema está roto».
   */
  if (!veOperacion) return <ResumenRestringido nombre={user?.fullName ?? ''} />;

  if (operacion.isLoading) return <Cargando />;
  if (operacion.isError) {
    return (
      <ErrorCarga
        mensaje={getErrorMessage(operacion.error)}
        onReintentar={() => void operacion.refetch()}
      />
    );
  }

  const r = operacion.data!.resumen;
  const sinAtender = reclamos.data?.length ?? 0;

  const alertas = [
    {
      clave: 'sin-domiciliario',
      cuantos: r.pedidos.sinDomiciliario,
      texto: 'sin domiciliario',
      detalle: 'Nadie los ha tomado. Revisa la flota o asígnalos a mano.',
      a: '/operacion',
    },
    {
      clave: 'sin-aceptar',
      cuantos: r.pedidos.sinAceptar,
      texto: 'esperando al comercio',
      detalle: 'El comercio todavía no los acepta. Puede que no haya nadie en el local.',
      a: '/operacion',
    },
    {
      clave: 'reclamos',
      cuantos: sinAtender,
      texto: sinAtender === 1 ? 'reclamo sin resolver' : 'reclamos sin resolver',
      detalle: 'Un cliente está esperando respuesta.',
      a: '/reclamos',
    },
  ].filter((a) => a.cuantos > 0);

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-3xl text-kick-dark">Hola, {user?.fullName.split(' ')[0]}</h1>
      <p className="mt-1 text-sm text-kick-gray">Administración de la plataforma</p>

      <InterruptorPlataforma />

      {alertas.length > 0 && (
        <div className="mt-6 space-y-2">
          {alertas.map((a) => (
            <Link key={a.clave} to={a.a} className="block">
              <div className="flex items-center gap-3 rounded-kick border border-amber-200 bg-amber-50 p-4 transition hover:border-amber-300">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-amber-900">
                    {a.cuantos} {a.texto}
                  </p>
                  <p className="text-sm text-amber-800">{a.detalle}</p>
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-amber-700" />
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metrica
          icono={<ClipboardList className="h-5 w-5" />}
          etiqueta="Pedidos en curso"
          valor={String(r.pedidos.activos)}
          detalle={`${r.hoy.entregados} entregados hoy`}
        />
        <Metrica
          icono={<Bike className="h-5 w-5" />}
          etiqueta="Flota en turno"
          valor={String(r.flota.enTurno)}
          detalle={`${r.flota.libres} libres · ${r.flota.ocupados} en pedido`}
          resaltado={r.flota.enTurno > 0}
        />
        <Metrica
          icono={<Store className="h-5 w-5" />}
          etiqueta="Comercios abiertos"
          valor={`${r.comercios.abiertos} de ${r.comercios.activos}`}
          detalle="recibiendo pedidos ahora"
        />
        <Metrica
          icono={<TrendingUp className="h-5 w-5" />}
          etiqueta="Ventas de hoy"
          valor={formatCOP(r.hoy.gmv)}
          detalle={`${formatCOP(r.hoy.comisiones)} de comisión`}
        />
      </div>

      {r.hoy.cancelados > 0 && (
        <p className="mt-3 text-sm text-kick-gray">
          Hoy se cancelaron {r.hoy.cancelados} de {r.hoy.pedidos} pedidos.
        </p>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Atajo a="/operacion" icono={<ClipboardList className="h-5 w-5" />} texto="Monitor de operación" />
        {puede('comercios', 'ver') && (
          <Atajo a="/comercios" icono={<Store className="h-5 w-5" />} texto="Crear un comercio" />
        )}
        {puede('domiciliarios', 'ver') && (
          <Atajo a="/domiciliarios" icono={<Bike className="h-5 w-5" />} texto="Verificar domiciliarios" />
        )}
        {puede('informes', 'ver') && (
          <Atajo a="/informes-plataforma" icono={<TrendingUp className="h-5 w-5" />} texto="Informes" />
        )}
      </div>
    </div>
  );
}

/**
 * El botón de encendido de la plataforma, en la portada.
 *
 * Es el control más gordo del negocio —cerrar el día, no atender un festivo—
 * y por eso no vive enterrado en Ajustes: está aquí, con confirmación antes
 * de apagar. El mensaje que ve el cliente se edita en Ajustes.
 */
function InterruptorPlataforma() {
  const puede = usePuede();
  const avisos = useAvisos();
  const ajustes = useAjustes();
  const guardar = useGuardarAjustes();
  const [confirmando, setConfirmando] = useState(false);

  if (!puede('ajustes', 'editar') || !ajustes.data) return null;

  const encendida = ajustes.data.platformOpen;

  const cambiar = (valor: boolean): void => {
    guardar.mutate(
      { platformOpen: valor } as Partial<AjustesPlataforma>,
      {
        onSuccess: () =>
          avisos.exito(valor ? 'Plataforma encendida: la tienda vuelve a operar' : 'Plataforma apagada'),
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
    setConfirmando(false);
  };

  return (
    <div
      className={cn(
        'mt-4 flex flex-wrap items-center gap-3 rounded-kick border p-4',
        encendida ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50',
      )}
    >
      <Power className={cn('h-6 w-6 shrink-0', encendida ? 'text-emerald-600' : 'text-amber-600')} />
      <div className="min-w-0 flex-1">
        <p className={cn('font-bold', encendida ? 'text-emerald-900' : 'text-amber-900')}>
          {encendida ? 'Plataforma encendida' : 'Plataforma apagada'}
        </p>
        <p className={cn('text-sm', encendida ? 'text-emerald-800' : 'text-amber-800')}>
          {encendida
            ? 'La tienda de clientes está recibiendo pedidos.'
            : 'La tienda muestra el aviso de fuera de servicio y no entran pedidos.'}
        </p>
      </div>
      <Boton
        variante={encendida ? 'peligro' : 'primario'}
        cargando={guardar.isPending}
        onClick={() => (encendida ? setConfirmando(true) : cambiar(true))}
      >
        {encendida ? 'Apagar' : 'Encender'}
      </Boton>

      <ConfirmarModal
        abierto={confirmando}
        titulo="Apagar la plataforma"
        mensaje="La tienda de clientes deja de recibir pedidos y muestra el aviso de fuera de servicio. Los pedidos que ya están en curso siguen su camino normal."
        textoConfirmar="Apagar"
        cargando={guardar.isPending}
        onCancelar={() => setConfirmando(false)}
        onConfirmar={() => cambiar(false)}
      />
    </div>
  );
}

/** Portada de quien administra una parte: solo los atajos a sus módulos. */
function ResumenRestringido({ nombre }: { nombre: string }) {
  const puede = usePuede();
  const atajos = ATAJOS_POR_MODULO.filter((a) => puede(MODULO_POR_RUTA[a.a]!, 'ver'));

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-3xl text-kick-dark">Hola, {nombre.split(' ')[0]}</h1>
      <p className="mt-1 text-sm text-kick-gray">Administración de la plataforma</p>

      {atajos.length === 0 ? (
        <p className="mt-6 rounded-kick bg-white p-5 text-sm text-kick-gray shadow-card">
          Tu cuenta todavía no tiene módulos asignados. Pídele al administrador principal que te
          dé acceso a lo que vas a manejar.
        </p>
      ) : (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {atajos.map((a) => (
            <Atajo key={a.a} a={a.a} icono={a.icono} texto={a.texto} />
          ))}
        </div>
      )}
    </div>
  );
}

const ATAJOS_POR_MODULO: { a: string; texto: string; icono: React.ReactNode }[] = [
  { a: '/programadas', texto: 'Encomiendas programadas', icono: <ClipboardList className="h-5 w-5" /> },
  { a: '/comercios', texto: 'Comercios', icono: <Store className="h-5 w-5" /> },
  { a: '/domiciliarios', texto: 'Domiciliarios', icono: <Bike className="h-5 w-5" /> },
  { a: '/usuarios-plataforma', texto: 'Usuarios', icono: <Store className="h-5 w-5" /> },
  { a: '/zonas', texto: 'Zonas y tarifas', icono: <TrendingUp className="h-5 w-5" /> },
  { a: '/cupones', texto: 'Cupones', icono: <TrendingUp className="h-5 w-5" /> },
  { a: '/anuncios', texto: 'Publicidad', icono: <TrendingUp className="h-5 w-5" /> },
  { a: '/soporte', texto: 'Soporte', icono: <ClipboardList className="h-5 w-5" /> },
  { a: '/reclamos', texto: 'Reclamos', icono: <ClipboardList className="h-5 w-5" /> },
  { a: '/cobros', texto: 'Cobros a comercios', icono: <TrendingUp className="h-5 w-5" /> },
  { a: '/liquidaciones', texto: 'Liquidaciones', icono: <TrendingUp className="h-5 w-5" /> },
  { a: '/informes-plataforma', texto: 'Informes', icono: <TrendingUp className="h-5 w-5" /> },
  { a: '/clasificacion', texto: 'Clasificación', icono: <TrendingUp className="h-5 w-5" /> },
  { a: '/ajustes', texto: 'Ajustes', icono: <Store className="h-5 w-5" /> },
  { a: '/auditoria', texto: 'Auditoría', icono: <ClipboardList className="h-5 w-5" /> },
];

function Atajo({ a, icono, texto }: { a: string; icono: React.ReactNode; texto: string }) {
  return (
    <Link to={a}>
      <div className="flex items-center gap-3 rounded-kick bg-white p-4 shadow-card transition hover:shadow-md">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray">
          {icono}
        </span>
        <span className="min-w-0 flex-1 text-sm font-semibold text-kick-ink">{texto}</span>
        <ArrowRight className="h-4 w-4 shrink-0 text-kick-gray" />
      </div>
    </Link>
  );
}

// ── Piezas ──────────────────────────────────────────────────

function Metrica({
  icono,
  etiqueta,
  valor,
  detalle,
  resaltado,
}: {
  icono: React.ReactNode;
  etiqueta: string;
  valor: string;
  detalle: string;
  resaltado?: boolean;
}) {
  return (
    <div className="rounded-kick bg-white p-4 shadow-card">
      <div
        className={cn(
          'grid h-9 w-9 place-items-center rounded-kick',
          resaltado ? 'bg-emerald-50 text-emerald-600' : 'bg-kick-light text-kick-gray',
        )}
      >
        {icono}
      </div>
      <p className="mt-3 text-2xl font-bold text-kick-dark">{valor}</p>
      <p className="text-sm font-semibold text-kick-ink">{etiqueta}</p>
      <p className="text-xs text-kick-gray">{detalle}</p>
    </div>
  );
}

function Aviso({
  tono,
  titulo,
  texto,
  accion,
}: {
  tono: 'alerta' | 'info';
  titulo: string;
  texto: string;
  accion?: { texto: string; a: string };
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-kick border px-4 py-3',
        tono === 'alerta'
          ? 'border-amber-200 bg-amber-50'
          : 'border-kick-gray/20 bg-white',
      )}
    >
      <AlertTriangle
        className={cn('h-5 w-5 shrink-0', tono === 'alerta' ? 'text-amber-600' : 'text-kick-gray')}
      />
      <div className="min-w-0 flex-1">
        <p className={cn('font-semibold', tono === 'alerta' ? 'text-amber-900' : 'text-kick-ink')}>
          {titulo}
        </p>
        <p className={cn('text-sm', tono === 'alerta' ? 'text-amber-700' : 'text-kick-gray')}>
          {texto}
        </p>
      </div>
      {accion && (
        <Link to={accion.a}>
          <Boton variante="secundario" className="text-xs">
            {accion.texto}
          </Boton>
        </Link>
      )}
    </div>
  );
}
